import { deduplicateAsync } from '@common/utils/deduplicateAsync'

import { getGatewaysInDateRange, removeGateway as _removeGateway } from '../gateways'
import type { GatewayTimeFilterInput } from '../gateways.interface'
import { resolveGatewayTimeFilterForApi } from '../utils/resolveGatewayTimeFilter'

import { useGatewaysStore } from './gateways.store'

const GATEWAYS_FETCH_TIMEOUT_MS = 15000
const FETCH_KEY_SEP = '\x1f'

let latestGatewaysRequestId = 0

async function withTimeout<T>(promise: Promise<T>, timeoutMs: number, message: string): Promise<T> {
  let timeoutHandle: ReturnType<typeof setTimeout> | null = null
  try {
    return await Promise.race([
      promise,
      new Promise<T>((_, reject) => {
        timeoutHandle = setTimeout(() => reject(new Error(message)), timeoutMs)
      }),
    ])
  } finally {
    if (timeoutHandle) {
      clearTimeout(timeoutHandle)
    }
  }
}

type ParsedGatewaysFetchKey = {
  projectId: string
  dateFrom: string
  dateTo: string
  scopedFilter: boolean
}

function buildGatewaysFetchKey(projectId: string, filter?: GatewayTimeFilterInput): string {
  const { dateFrom, dateTo } = resolveGatewayTimeFilterForApi(filter)
  const scopedFilter = filter != null
  return [projectId, dateFrom, dateTo, scopedFilter ? '1' : '0'].join(FETCH_KEY_SEP)
}

function parseGatewaysFetchKey(requestKey: string): ParsedGatewaysFetchKey | null {
  const [projectId, dateFrom, dateTo, scopedFlag] = requestKey.split(FETCH_KEY_SEP)
  if (!projectId || dateFrom == null || dateTo == null) {
    return null
  }

  return {
    projectId,
    dateFrom,
    dateTo,
    scopedFilter: scopedFlag === '1',
  }
}

function resolveProjectHasGateways(gatewayCount: number, scopedFilter: boolean): boolean {
  if (gatewayCount > 0) {
    return true
  }

  return scopedFilter
}

const runGatewaysFetch = deduplicateAsync(async (requestKey: string): Promise<void> => {
  const parsed = parseGatewaysFetchKey(requestKey)
  if (!parsed) {
    return
  }

  const { projectId, dateFrom, dateTo, scopedFilter } = parsed
  const requestId = ++latestGatewaysRequestId
  const { setState, lastFetchedProjectId: previousProjectId } = useGatewaysStore.getState()
  const isProjectChange = String(previousProjectId) !== String(projectId)

  setState({
    loading: true,
    error: null,
    ...(isProjectChange ? { projectHasGateways: null } : {}),
  })

  try {
    const data = await withTimeout(
      getGatewaysInDateRange(projectId, dateFrom, dateTo),
      GATEWAYS_FETCH_TIMEOUT_MS,
      'Timed out while loading gateways'
    )

    if (requestId !== latestGatewaysRequestId) {
      return
    }

    setState({
      loading: false,
      gateways: data,
      lastFetchedProjectId: projectId,
      projectHasGateways: resolveProjectHasGateways(data.length, scopedFilter),
    })
  } catch (err) {
    if (requestId !== latestGatewaysRequestId) {
      return
    }

    setState({
      gateways: [],
      lastFetchedProjectId: null,
      projectHasGateways: null,
      error: err instanceof Error ? err.message : 'Failed to load gateways',
    })
    throw err
  } finally {
    if (requestId === latestGatewaysRequestId) {
      setState({ loading: false })
    }
  }
})

export async function getGateways(projectId: string, filter?: GatewayTimeFilterInput): Promise<void> {
  if (!projectId) {
    return
  }

  await runGatewaysFetch(buildGatewaysFetchKey(projectId, filter))
}

export function resetGatewaysState(): void {
  latestGatewaysRequestId += 1
  useGatewaysStore.getState().setState({
    loading: false,
    error: null,
    gateways: [],
    lastFetchedProjectId: null,
    projectHasGateways: null,
  })
}

export async function removeGateway(
  gatewayId: string,
  projectId: string,
  filter?: GatewayTimeFilterInput
): Promise<void> {
  await _removeGateway(gatewayId)
  await getGateways(projectId, filter)
}
